import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { aiSecurityAPI, securityAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { Link } from 'react-router-dom';

interface AISuggestion {
  id: number;
  issue_type: string;
  title: string;
  severity: 'info' | 'low' | 'medium' | 'high' | 'critical';
  description: string;
  ai_recommendation: string;
  action_steps: string[];
  affected_url: string;
  context: any;
  is_dismissed: boolean;
  applied_at: string | null;
  created_at: string;
}

interface MalwareDetection {
  id: number;
  url: string;
  signature_name: string;
  severity: string;
  matched_content: string;
  status: string;
  detected_at: string;
}

interface AIAnalysis {
  changeSummary: string;
  anomalies: string[];
  riskLevel: 'safe' | 'low' | 'medium' | 'high' | 'critical';
  confidence: number;
  adInjection: boolean;
  malwareIndicators: boolean;
  trackingChanges: string | null;
}

interface ScanProgress {
  inProgress: boolean;
  stage: string | null;
  status: string | null;
  pagesScanned: number;
  totalPages: number;
  aiAnalyses: number;
  sslIssues: number;
  issuesFound: number;
  startedAt: string | null;
  completedAt: string | null;
  elapsed: number;
  error: string | null;
}

interface PageChange {
  id: number;
  url: string;
  change_type: string;
  change_percent: number;
  suspicious_changes: string[];
  changes_summary: {
    scriptsAdded?: number;
    scriptsRemoved?: number;
    iframesAdded?: number;
    iframesRemoved?: number;
    externalLinksAdded?: number;
    externalLinksRemoved?: number;
    formsModified?: number;
    formsAdded?: number;
    formsRemoved?: number;
  } | null;
  ai_analysis: AIAnalysis | null;
  ai_risk_level: string | null;
  ai_analyzed_at: string | null;
  is_suspicious: boolean;
  is_reviewed: boolean;
  detected_at: string;
}

interface ScanHistoryEntry {
  date: string;
  changes_detected: number;
  malware_detected: number;
  suggestions_generated: number;
  pages_scanned: number;
}

interface SeverityEntry {
  severity: string;
  count: number;
}

interface LoginSecurity {
  lastScanAt: string;
  loginFormsCount: number;
  adminPanelsFound: number;
  exposedAdminPanels: number;
  overallRisk: string;
  criticalFindings: any[];
  recommendations: string[];
}

interface AuditHistoryEntry {
  id: number;
  date: string;
  score: number;
  riskLevel: string;
  scannedAt: string;
}

interface Overview {
  changes: { total: number; unreviewed: number };
  malware: { total: number; active: number };
  suggestions: { total: number; pending: number };
  lastScanAt?: string | null;
  scanHistory?: ScanHistoryEntry[];
  severityDistribution?: SeverityEntry[];
  loginSecurity?: LoginSecurity | null;
  auditHistory?: AuditHistoryEntry[];
}

interface MonitoredPage {
  id: number;
  url: string;
  pageType: string;
  importance: string;
  importanceScore: number;
  category: string;
  lastChecked: string;
  status: 'healthy' | 'changed' | 'suspicious' | 'threatened';
  changesCount: number;
  suspiciousCount: number;
  maxChangePercent: number;
  activeMalware: number;
  malwareSeverity: string | null;
}

interface MonitoredPagesSummary {
  total: number;
  healthy: number;
  changed: number;
  suspicious: number;
  threatened: number;
}

interface TrafficAnomaly {
  type: string;
  severity: string;
  timestamp: string;
  description: string;
  value: number;
  baseline: number;
  deviationPercent: number;
}

interface TrafficSummary {
  totalPageViews: number;
  avgHourlyViews: number;
  peakHour: string | null;
  peakViews: number;
  currentHourViews: number;
  trafficTrend: string;
  riskLevel: string;
}

interface TrafficAnalysis {
  hourlyData: Array<{ hour: string; pageViews: number; uniqueVisitors: number }>;
  anomalies: TrafficAnomaly[];
  summary: TrafficSummary;
}

interface EndpointInfo {
  url: string;
  hostname: string;
  ipv4: string[];
  ipv6: string[];
}

interface ScanRecord {
  id: number;
  scanType: string;
  status: string;
  stage: string | null;
  pagesScanned: number;
  issuesFound: number;
  aiAnalyses: number;
  sslIssues: number;
  changesDetected: number;
  malwareDetected: number;
  suggestionsGenerated: number;
  duration: number;
  startedAt: string;
  completedAt: string | null;
  error: string | null;
}

interface ChangeDetail {
  id: number;
  url: string;
  change_type: string;
  change_percent: number;
  changes_summary: any;
  suspicious_changes: string[];
  ai_analysis: AIAnalysis | null;
  current_content: string | null;
  previous_content: string | null;
}

interface AuditScore {
  overallScore: number;
  malwareScore: number;
  changeScore: number;
  integrityScore: number;
  riskLevel: string;
  riskFactors: string[];
  recommendations: string[];
}

interface AuditData {
  id: number;
  assetId: number;
  auditDate: string;
  score: AuditScore;
  metrics: {
    pagesScanned: number;
    malwareDetected: number;
    criticalChanges: number;
    suspiciousChanges: number;
    totalChanges: number;
  };
  scanDuration: number;
  scannedAt: string;
}

interface SecurityReport {
  security_score: number;
  security_grade: string;
  statistics: {
    total_scans: number;
    total_issues: number;
    open_issues: number;
    resolved_issues: number;
    issues_by_severity: { critical: number; high: number; medium: number; low: number };
  };
  vulnerability_assessment: {
    overall_score: number;
    risk_level: string;
    categories: Record<string, { score: number; status: string; issues: any[] }>;
    recommendations: any[];
  } | null;
  scan_history: any[];
}

// Score Ring SVG
const ScoreRing: React.FC<{ score: number; size?: number; strokeWidth?: number; label?: string }> = ({
  score, size = 140, strokeWidth = 14, label = 'Score'
}) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (score / 100) * circumference;
  const center = size / 2;

  const getColor = (s: number) => {
    if (s >= 80) return '#10b981';
    if (s >= 60) return '#f59e0b';
    if (s >= 40) return '#f97316';
    return '#ef4444';
  };

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={center} cy={center} r={radius} fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
      <circle
        cx={center} cy={center} r={radius} fill="none"
        stroke={getColor(score)} strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        transform={`rotate(-90 ${center} ${center})`}
        style={{ transition: 'stroke-dashoffset 0.6s ease' }}
      />
      <text x={center} y={center - 8} textAnchor="middle" dominantBaseline="middle" fontSize="32" fontWeight="bold" fill="#1f2937">{Math.round(score)}</text>
      <text x={center} y={center + 16} textAnchor="middle" dominantBaseline="middle" fontSize="11" fill="#6b7280">{label}</text>
    </svg>
  );
};

// Mini Score Bar
const MiniScoreBar: React.FC<{ score: number; label: string; color: string }> = ({ score, label, color }) => (
  <div className="mb-2">
    <div className="d-flex justify-content-between mb-1">
      <small className="text-muted">{label}</small>
      <small className="fw-semibold">{Math.round(score)}/100</small>
    </div>
    <div className="progress" style={{ height: 6 }}>
      <div className="progress-bar" style={{ width: `${score}%`, backgroundColor: color, transition: 'width 0.6s ease' }}></div>
    </div>
  </div>
);

// SVG Donut Chart
const SeverityDonutChart: React.FC<{ data: SeverityEntry[] }> = ({ data }) => {
  const total = data.reduce((sum, d) => sum + d.count, 0);
  const colors: Record<string, string> = { critical: '#dc3545', high: '#fd7e14', medium: '#ffc107', low: '#6c757d', info: '#0dcaf0' };
  if (total === 0) {
    return (
      <div className="text-center py-3">
        <svg width="130" height="130" viewBox="0 0 130 130">
          <circle cx="65" cy="65" r="50" fill="none" stroke="#e9ecef" strokeWidth="16" />
          <text x="65" y="65" textAnchor="middle" dominantBaseline="middle" fontSize="20" fontWeight="bold" fill="#6c757d">0</text>
        </svg>
        <div className="text-muted small mt-1">No active issues</div>
      </div>
    );
  }
  const radius = 50;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  return (
    <div className="text-center">
      <svg width="130" height="130" viewBox="0 0 130 130">
        {data.map((entry, i) => {
          const pct = entry.count / total;
          const dashLength = pct * circumference;
          const currentOffset = offset;
          offset += dashLength;
          return (
            <circle key={i} cx="65" cy="65" r={radius} fill="none" stroke={colors[entry.severity] || '#6c757d'}
              strokeWidth="16" strokeDasharray={`${dashLength} ${circumference - dashLength}`}
              strokeDashoffset={-currentOffset} transform="rotate(-90 65 65)" />
          );
        })}
        <text x="65" y="60" textAnchor="middle" dominantBaseline="middle" fontSize="22" fontWeight="bold" fill="#212529">{total}</text>
        <text x="65" y="80" textAnchor="middle" dominantBaseline="middle" fontSize="10" fill="#6c757d">issues</text>
      </svg>
      <div className="d-flex justify-content-center gap-2 mt-1 flex-wrap">
        {data.filter(d => d.count > 0).map((entry, i) => (
          <div key={i} className="d-flex align-items-center">
            <span className="d-inline-block rounded-circle me-1" style={{ width: 7, height: 7, backgroundColor: colors[entry.severity] || '#6c757d' }}></span>
            <small className="text-muted text-capitalize" style={{ fontSize: '0.7rem' }}>{entry.severity} ({entry.count})</small>
          </div>
        ))}
      </div>
    </div>
  );
};

// Scan Activity Bar Chart
const ScanActivityChart: React.FC<{ data: ScanHistoryEntry[] }> = ({ data }) => {
  const reversed = [...data].reverse().slice(-10);
  if (reversed.length === 0) {
    return (
      <div className="text-center py-3 text-muted">
        <i className="bi bi-bar-chart fs-2 d-block mb-1"></i>
        <small>No scan activity yet</small>
      </div>
    );
  }
  const maxVal = Math.max(...reversed.map(d => d.changes_detected + d.malware_detected + d.suggestions_generated), 1);
  return (
    <div>
      <div className="d-flex align-items-end justify-content-between" style={{ height: 100, gap: 3 }}>
        {reversed.map((entry, i) => {
          const total = entry.changes_detected + entry.malware_detected + entry.suggestions_generated;
          const height = Math.max((total / maxVal) * 100, 4);
          return (
            <div key={i} className="flex-fill d-flex flex-column align-items-center"
              title={`${new Date(entry.date).toLocaleDateString()}: ${total} findings`}>
              <small className="text-muted mb-1" style={{ fontSize: '0.6rem' }}>{total || ''}</small>
              <div className="rounded-top w-100" style={{
                height: `${height}%`, minHeight: 3,
                background: entry.malware_detected > 0 ? 'linear-gradient(180deg, #dc3545, #fd7e14)'
                  : total > 0 ? 'linear-gradient(180deg, #6366f1, #818cf8)' : '#e9ecef',
              }}></div>
              <small className="text-muted mt-1" style={{ fontSize: '0.55rem' }}>
                {new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </small>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// CSS keyframes for pulse animations (injected once)
const pulseStyles = document.createElement('style');
if (!document.getElementById('security-pulse-styles')) {
  pulseStyles.id = 'security-pulse-styles';
  pulseStyles.textContent = `
    @keyframes pulse-green { 0%, 100% { box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4); } 50% { box-shadow: 0 0 8px 3px rgba(16, 185, 129, 0.2); } }
    @keyframes pulse-orange { 0%, 100% { box-shadow: 0 0 0 0 rgba(253, 126, 20, 0.4); } 50% { box-shadow: 0 0 8px 3px rgba(253, 126, 20, 0.3); } }
    @keyframes pulse-red { 0%, 100% { box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.5); } 50% { box-shadow: 0 0 10px 4px rgba(220, 53, 69, 0.3); } }
    @keyframes sweep { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes gauge-needle { 0% { transform: rotate(-90deg); } 100% { transform: rotate(var(--needle-angle)); } }
    @keyframes fade-in-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
    .page-tile { animation: fade-in-up 0.3s ease forwards; opacity: 0; }
    .page-tile:hover { transform: scale(1.08); z-index: 2; }
    .pulse-healthy { animation: pulse-green 2.5s ease-in-out infinite; }
    .pulse-changed { animation: pulse-orange 2s ease-in-out infinite; }
    .pulse-suspicious { animation: pulse-orange 1.5s ease-in-out infinite; }
    .pulse-threatened { animation: pulse-red 1.2s ease-in-out infinite; }
  `;
  document.head.appendChild(pulseStyles);
}

// Threat Level Gauge (animated speedometer)
const ThreatLevelGauge: React.FC<{ threatScore: number; totalFindings: number; activeMalware: number; suspiciousPages: number }> = ({
  threatScore, totalFindings, activeMalware, suspiciousPages
}) => {
  const size = 180;
  const cx = size / 2;
  const cy = size / 2 + 10;
  const radius = 70;
  const startAngle = -210;
  const endAngle = 30;
  const totalArc = endAngle - startAngle; // 240 degrees

  // Clamp threat score 0-100
  const score = Math.max(0, Math.min(100, threatScore));
  const needleAngle = startAngle + (score / 100) * totalArc;

  // Arc path helper
  const polarToCartesian = (cx: number, cy: number, r: number, angleDeg: number) => {
    const rad = (angleDeg * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  };

  const describeArc = (cx: number, cy: number, r: number, start: number, end: number) => {
    const s = polarToCartesian(cx, cy, r, start);
    const e = polarToCartesian(cx, cy, r, end);
    const largeArc = end - start > 180 ? 1 : 0;
    return `M ${s.x} ${s.y} A ${r} ${r} 0 ${largeArc} 1 ${e.x} ${e.y}`;
  };

  const getColor = (s: number) => {
    if (s <= 20) return '#10b981';
    if (s <= 40) return '#84cc16';
    if (s <= 60) return '#f59e0b';
    if (s <= 80) return '#f97316';
    return '#ef4444';
  };

  const getLabel = (s: number) => {
    if (s <= 20) return 'Low';
    if (s <= 40) return 'Guarded';
    if (s <= 60) return 'Elevated';
    if (s <= 80) return 'High';
    return 'Critical';
  };

  const needleEnd = polarToCartesian(cx, cy, radius - 12, needleAngle);

  return (
    <div className="text-center">
      <svg width={size} height={size * 0.7} viewBox={`0 0 ${size} ${size * 0.7}`}>
        {/* Background arc */}
        <path d={describeArc(cx, cy, radius, startAngle, endAngle)} fill="none" stroke="#e5e7eb" strokeWidth="18" strokeLinecap="round" />
        {/* Color segments */}
        <path d={describeArc(cx, cy, radius, startAngle, startAngle + totalArc * 0.2)} fill="none" stroke="#10b981" strokeWidth="18" strokeLinecap="round" opacity="0.3" />
        <path d={describeArc(cx, cy, radius, startAngle + totalArc * 0.2, startAngle + totalArc * 0.4)} fill="none" stroke="#84cc16" strokeWidth="18" opacity="0.3" />
        <path d={describeArc(cx, cy, radius, startAngle + totalArc * 0.4, startAngle + totalArc * 0.6)} fill="none" stroke="#f59e0b" strokeWidth="18" opacity="0.3" />
        <path d={describeArc(cx, cy, radius, startAngle + totalArc * 0.6, startAngle + totalArc * 0.8)} fill="none" stroke="#f97316" strokeWidth="18" opacity="0.3" />
        <path d={describeArc(cx, cy, radius, startAngle + totalArc * 0.8, endAngle)} fill="none" stroke="#ef4444" strokeWidth="18" strokeLinecap="round" opacity="0.3" />
        {/* Active arc */}
        {score > 0 && (
          <path d={describeArc(cx, cy, radius, startAngle, startAngle + (score / 100) * totalArc)}
            fill="none" stroke={getColor(score)} strokeWidth="18" strokeLinecap="round"
            style={{ transition: 'all 1s ease-in-out' }} />
        )}
        {/* Needle */}
        <line x1={cx} y1={cy} x2={needleEnd.x} y2={needleEnd.y}
          stroke="#1f2937" strokeWidth="2.5" strokeLinecap="round"
          style={{ transition: 'all 1s ease-in-out' }} />
        <circle cx={cx} cy={cy} r="5" fill="#1f2937" />
        {/* Center text */}
        <text x={cx} y={cy - 18} textAnchor="middle" fontSize="26" fontWeight="bold" fill={getColor(score)}
          style={{ transition: 'fill 0.5s ease' }}>{Math.round(score)}</text>
        <text x={cx} y={cy - 2} textAnchor="middle" fontSize="9" fill="#6b7280">THREAT LEVEL</text>
      </svg>
      <div className="mt-1">
        <span className="badge px-3 py-1" style={{ backgroundColor: getColor(score), color: '#fff', fontSize: '0.8rem' }}>
          {getLabel(score)}
        </span>
      </div>
      <div className="d-flex justify-content-center gap-3 mt-2">
        <div className="text-center">
          <div className="fw-bold text-danger" style={{ fontSize: '1.1rem' }}>{activeMalware}</div>
          <small className="text-muted" style={{ fontSize: '0.7rem' }}>Threats</small>
        </div>
        <div className="text-center">
          <div className="fw-bold text-warning" style={{ fontSize: '1.1rem' }}>{suspiciousPages}</div>
          <small className="text-muted" style={{ fontSize: '0.7rem' }}>Suspicious</small>
        </div>
        <div className="text-center">
          <div className="fw-bold text-primary" style={{ fontSize: '1.1rem' }}>{totalFindings}</div>
          <small className="text-muted" style={{ fontSize: '0.7rem' }}>Findings</small>
        </div>
      </div>
    </div>
  );
};

// Live Page Monitor Grid
const PageMonitorGrid: React.FC<{ pages: MonitoredPage[]; summary: MonitoredPagesSummary }> = ({ pages, summary }) => {
  const statusConfig = {
    healthy: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.15)', border: 'rgba(16, 185, 129, 0.4)', icon: 'bi-check-circle-fill', pulse: 'pulse-healthy' },
    changed: { color: '#fd7e14', bg: 'rgba(253, 126, 20, 0.15)', border: 'rgba(253, 126, 20, 0.4)', icon: 'bi-arrow-repeat', pulse: 'pulse-changed' },
    suspicious: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.15)', border: 'rgba(245, 158, 11, 0.5)', icon: 'bi-exclamation-triangle-fill', pulse: 'pulse-suspicious' },
    threatened: { color: '#dc3545', bg: 'rgba(220, 53, 69, 0.15)', border: 'rgba(220, 53, 69, 0.5)', icon: 'bi-shield-exclamation', pulse: 'pulse-threatened' },
  };

  const extractPath = (url: string) => {
    try {
      const u = new URL(url);
      return u.pathname === '/' ? u.hostname : u.pathname;
    } catch { return url; }
  };

  if (pages.length === 0) {
    return (
      <div className="text-center py-4">
        <i className="bi bi-globe2 text-muted fs-2 d-block mb-2"></i>
        <small className="text-muted">No pages monitored yet.<br />Run a scan to start monitoring.</small>
      </div>
    );
  }

  return (
    <div>
      {/* Summary bar */}
      <div className="d-flex gap-2 mb-3 flex-wrap">
        {summary.healthy > 0 && <span className="badge bg-success bg-opacity-10 text-success"><i className="bi bi-check-circle me-1"></i>{summary.healthy} Healthy</span>}
        {summary.changed > 0 && <span className="badge bg-warning bg-opacity-10 text-warning"><i className="bi bi-arrow-repeat me-1"></i>{summary.changed} Changed</span>}
        {summary.suspicious > 0 && <span className="badge bg-warning bg-opacity-25 text-dark"><i className="bi bi-exclamation-triangle me-1"></i>{summary.suspicious} Suspicious</span>}
        {summary.threatened > 0 && <span className="badge bg-danger bg-opacity-10 text-danger"><i className="bi bi-shield-exclamation me-1"></i>{summary.threatened} Threats</span>}
      </div>
      {/* Grid */}
      <div className="d-flex flex-wrap gap-2" style={{ maxHeight: 220, overflowY: 'auto', overflowX: 'hidden' }}>
        {pages.map((page, i) => {
          const cfg = statusConfig[page.status];
          return (
            <div key={page.id}
              className={`page-tile ${cfg.pulse} rounded-3 d-flex align-items-center gap-2 px-2 py-1`}
              style={{
                animationDelay: `${i * 0.05}s`,
                background: cfg.bg,
                border: `1px solid ${cfg.border}`,
                cursor: 'default',
                minWidth: 140, maxWidth: 200,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              }}
              title={`${page.url}\nStatus: ${page.status}\nChanges: ${page.changesCount} | Malware: ${page.activeMalware}\nLast checked: ${new Date(page.lastChecked).toLocaleString()}`}
            >
              <i className={`bi ${cfg.icon}`} style={{ color: cfg.color, fontSize: '0.85rem' }}></i>
              <div className="flex-grow-1 text-truncate">
                <div className="text-truncate fw-medium" style={{ fontSize: '0.72rem', color: '#1f2937' }}>{extractPath(page.url)}</div>
                <div style={{ fontSize: '0.6rem', color: '#6b7280' }}>
                  {page.category !== 'other' ? page.category : page.pageType}
                </div>
              </div>
              {page.activeMalware > 0 && (
                <span className="badge bg-danger" style={{ fontSize: '0.55rem' }}>{page.activeMalware}</span>
              )}
              {page.status === 'changed' && page.maxChangePercent > 0 && (
                <span className="badge bg-warning text-dark" style={{ fontSize: '0.55rem' }}>{parseFloat(String(page.maxChangePercent)).toFixed(0)}%</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const AISecurityAdvisorPage: React.FC = () => {
  useDocumentTitle('Intelligent Monitoring');
  const { currentAsset } = useAuth();
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [auditData, setAuditData] = useState<AuditData | null>(null);
  const [securityReport, setSecurityReport] = useState<SecurityReport | null>(null);
  const [suggestions, setSuggestions] = useState<AISuggestion[]>([]);
  const [malwareDetections, setMalwareDetections] = useState<MalwareDetection[]>([]);
  const [pageChanges, setPageChanges] = useState<PageChange[]>([]);
  const [monitoredPages, setMonitoredPages] = useState<MonitoredPage[]>([]);
  const [pagesSummary, setPagesSummary] = useState<MonitoredPagesSummary>({ total: 0, healthy: 0, changed: 0, suspicious: 0, threatened: 0 });
  const [activeTab, setActiveTab] = useState<'suggestions' | 'malware' | 'changes' | 'exposed-files' | 'history'>('suggestions');
  const [sensitiveFiles, setSensitiveFiles] = useState<any[]>([]);
  const [sensitiveFilesLoaded, setSensitiveFilesLoaded] = useState(false);
  const [expandedSuggestion, setExpandedSuggestion] = useState<number | null>(null);
  const [scanProgress, setScanProgress] = useState<ScanProgress | null>(null);
  const [endpointInfo, setEndpointInfo] = useState<EndpointInfo[]>([]);
  const [scanRecords, setScanRecords] = useState<ScanRecord[]>([]);
  const [selectedChange, setSelectedChange] = useState<ChangeDetail | null>(null);
  const [showDiffModal, setShowDiffModal] = useState(false);
  const [loadingDiff, setLoadingDiff] = useState(false);
  const [selectedScanDetail, setSelectedScanDetail] = useState<ScanRecord | null>(null);
  const [trafficAnalysis, setTrafficAnalysis] = useState<TrafficAnalysis | null>(null);
  const [networkScan, setNetworkScan] = useState<any>(null);
  const [wordpressScan, setWordpressScan] = useState<any>(null);
  const pollRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (currentAsset) {
      loadData();
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [currentAsset]);

  const loadData = useCallback(async () => {
    if (!currentAsset) return;
    setLoading(true);
    try {
      const [overviewRes, suggestionsRes, malwareRes, changesRes, auditRes, reportRes, pagesRes] = await Promise.all([
        aiSecurityAPI.getOverview(currentAsset.id),
        aiSecurityAPI.getSuggestions(currentAsset.id, { dismissed: false }),
        aiSecurityAPI.getMalwareDetections(currentAsset.id, { days: 30 }),
        aiSecurityAPI.getChanges(currentAsset.id, { days: 30 }),
        aiSecurityAPI.getAuditScore(currentAsset.id).catch(() => null),
        securityAPI.getFullReport(currentAsset.id).catch(() => null),
        aiSecurityAPI.getMonitoredPages(currentAsset.id).catch(() => null),
      ]);

      if (overviewRes.data.status === 'success') setOverview(overviewRes.data.data);
      if (suggestionsRes.data.status === 'success') setSuggestions(suggestionsRes.data.data?.suggestions || suggestionsRes.data.data || []);
      if (malwareRes.data.status === 'success') setMalwareDetections(malwareRes.data.data?.detections || malwareRes.data.data || []);
      if (changesRes.data.status === 'success') setPageChanges(changesRes.data.data?.changes || changesRes.data.data || []);
      if (auditRes?.data?.status === 'success' && auditRes.data.data?.auditScore) setAuditData(auditRes.data.data.auditScore);
      if (reportRes?.data?.status === 'success') setSecurityReport(reportRes.data.data);
      if (pagesRes?.data?.status === 'success') {
        setMonitoredPages(pagesRes.data.data?.pages || []);
        setPagesSummary(pagesRes.data.data?.summary || { total: 0, healthy: 0, changed: 0, suspicious: 0, threatened: 0 });
      }

      // Load endpoint info, scan records, traffic analysis, network scan, and WP scan in parallel
      const [epRes, scanHistRes, trafficRes, networkRes, wpRes] = await Promise.all([
        aiSecurityAPI.getEndpointInfo(currentAsset.id).catch(() => null),
        aiSecurityAPI.getScanHistory(currentAsset.id).catch(() => null),
        aiSecurityAPI.getTrafficAnalysis(currentAsset.id).catch(() => null),
        aiSecurityAPI.getNetworkScan(currentAsset.id).catch(() => null),
        aiSecurityAPI.getWordPressScan(currentAsset.id).catch(() => null),
      ]);

      if (epRes?.data?.status === 'success') {
        setEndpointInfo(epRes.data.data?.endpoints || []);
      }
      if (scanHistRes?.data?.status === 'success') {
        setScanRecords(scanHistRes.data.data?.scans || []);
      }
      if (trafficRes?.data?.status === 'success') {
        setTrafficAnalysis(trafficRes.data.data || null);
      }
      if (networkRes?.data?.status === 'success') {
        setNetworkScan(networkRes.data.data?.results || null);
      }
      if (wpRes?.data?.status === 'success') {
        setWordpressScan(wpRes.data.data?.result || null);
      }

      // Load sensitive files
      try {
        const sfRes = await aiSecurityAPI.getSensitiveFiles(currentAsset.id);
        if (sfRes?.data?.status === 'success') {
          setSensitiveFiles(sfRes.data.data?.findings || []);
          setSensitiveFilesLoaded(true);
        }
      } catch { /* ignore */ }
    } catch (error: any) {
      if (error.response?.status !== 401) {
        console.error('Failed to load security data:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [currentAsset]);

  const triggerAIScan = async () => {
    if (!currentAsset) return;
    setScanning(true);
    setScanProgress(null);
    try {
      const response = await aiSecurityAPI.triggerScan(currentAsset.id);
      if (response.data.status === 'success') {
        toast.success('AI security scan started!');
        let pollCount = 0;
        // Poll scan progress every 3 seconds, up to 60 times (~3 min)
        pollRef.current = setInterval(async () => {
          pollCount++;
          try {
            const res = await aiSecurityAPI.getScanProgress(currentAsset.id);
            if (res.data.status === 'success') {
              const progress = res.data.data as ScanProgress;
              setScanProgress(progress);

              if (!progress.inProgress || pollCount >= 60) {
                if (pollRef.current) clearInterval(pollRef.current);
                pollRef.current = null;
                setScanning(false);
                if (progress.status === 'completed') {
                  toast.success('AI security scan completed!');
                } else if (progress.status === 'failed') {
                  toast.error(`Scan failed: ${progress.error || 'Unknown error'}`);
                } else {
                  toast.success('Scan may still be processing. Refreshing data...');
                }
                loadData();
              }
            }
          } catch (e) { /* ignore polling errors */ }
        }, 3000);
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to start scan';
      toast.error(`Scan failed: ${errorMsg}`);
      setScanning(false);
    }
  };

  const dismissSuggestion = async (suggestionId: number) => {
    if (!currentAsset) return;
    try {
      await aiSecurityAPI.dismissSuggestion(currentAsset.id, suggestionId);
      setSuggestions(suggestions.filter(s => s.id !== suggestionId));
      toast.success('Suggestion dismissed');
    } catch { toast.error('Failed to dismiss suggestion'); }
  };

  const applySuggestion = async (suggestionId: number) => {
    if (!currentAsset) return;
    try {
      await aiSecurityAPI.applySuggestion(currentAsset.id, suggestionId);
      setSuggestions(suggestions.map(s => s.id === suggestionId ? { ...s, applied_at: new Date().toISOString() } : s));
      toast.success('Marked as applied');
    } catch { toast.error('Failed to mark as applied'); }
  };

  const resolveMalware = async (detectionId: number) => {
    if (!currentAsset) return;
    try {
      await aiSecurityAPI.resolveMalware(currentAsset.id, detectionId);
      setMalwareDetections(malwareDetections.map(d => d.id === detectionId ? { ...d, status: 'resolved' } : d));
      toast.success('Marked as resolved');
    } catch { toast.error('Failed to resolve'); }
  };

  const markFalsePositive = async (detectionId: number) => {
    if (!currentAsset) return;
    try {
      await aiSecurityAPI.markFalsePositive(currentAsset.id, detectionId);
      setMalwareDetections(malwareDetections.map(d => d.id === detectionId ? { ...d, status: 'false_positive' } : d));
      toast.success('Marked as false positive');
    } catch { toast.error('Failed to mark as false positive'); }
  };

  const viewChangeDiff = async (changeId: number) => {
    if (!currentAsset) return;
    setLoadingDiff(true);
    setShowDiffModal(true);
    try {
      const res = await aiSecurityAPI.getChangeDetail(currentAsset.id, changeId);
      if (res.data.status === 'success') {
        setSelectedChange(res.data.data);
      }
    } catch { toast.error('Failed to load change details'); }
    finally { setLoadingDiff(false); }
  };

  const reviewChange = async (changeId: number) => {
    if (!currentAsset) return;
    try {
      await aiSecurityAPI.reviewChange(currentAsset.id, changeId);
      setPageChanges(pageChanges.map(c => c.id === changeId ? { ...c, is_reviewed: true } : c));
      toast.success('Marked as reviewed');
    } catch { toast.error('Failed to mark as reviewed'); }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'bi-exclamation-octagon-fill';
      case 'high': return 'bi-exclamation-triangle-fill';
      case 'medium': return 'bi-exclamation-circle-fill';
      case 'low': return 'bi-info-circle-fill';
      default: return 'bi-info-circle';
    }
  };

  const totalFindings = useMemo(() =>
    (overview?.suggestions?.pending || 0) + (overview?.malware?.active || 0) + (overview?.changes?.unreviewed || 0),
    [overview]
  );

  const monitoringStatus = useMemo(() => {
    if (scanning) return { label: 'Scanning...', color: 'primary', icon: 'bi-arrow-repeat' };
    if (!overview?.lastScanAt) return { label: 'Not Scanned', color: 'secondary', icon: 'bi-dash-circle' };
    if ((overview?.malware?.active || 0) > 0) return { label: 'Threats Detected', color: 'danger', icon: 'bi-shield-exclamation' };
    if (totalFindings > 0) return { label: 'Attention Required', color: 'warning', icon: 'bi-exclamation-triangle' };
    return { label: 'All Clear', color: 'success', icon: 'bi-shield-check' };
  }, [overview, totalFindings, scanning]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">

        {/* ===== TOP: Monitoring Header ===== */}
        <div className="card border-0 shadow-sm mb-4" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)', borderRadius: 12 }}>
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: 44, height: 44, background: 'rgba(99, 102, 241, 0.2)', border: '2px solid rgba(99, 102, 241, 0.4)' }}>
                  <i className="bi bi-brain text-white fs-5"></i>
                </div>
                <div>
                  <h1 className="h4 mb-0 text-white">Intelligent Monitoring</h1>
                  <p className="text-white-50 mb-0 small">Real-time security monitoring powered by AI</p>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3">
                <span className={`badge bg-${monitoringStatus.color} px-3 py-2`}>
                  <i className={`bi ${monitoringStatus.icon} me-1`}></i>{monitoringStatus.label}
                </span>
                <span className="text-white-50 small">
                  <i className="bi bi-clock me-1"></i>
                  Last scan: {overview?.lastScanAt
                    ? new Date(overview.lastScanAt).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
                    : 'Never'}
                </span>
                <button className="btn btn-outline-light btn-sm" onClick={loadData}>
                  <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                </button>
                <button className="btn btn-light btn-sm fw-semibold" onClick={triggerAIScan} disabled={scanning}>
                  {scanning ? (
                    <><span className="spinner-border spinner-border-sm me-1"></span>Scanning...</>
                  ) : (
                    <><i className="bi bi-play-circle me-1"></i>Run Manual Scan</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Scan Progress Bar */}
        {scanning && (() => {
          const stages = [
            { key: 'initializing', label: 'Initializing', icon: 'bi-gear' },
            { key: 'crawling', label: 'Crawling Pages', icon: 'bi-globe2' },
            { key: 'analyzing_changes', label: 'Analyzing Changes', icon: 'bi-file-diff' },
            { key: 'scanning_malware', label: 'Malware Scan', icon: 'bi-bug' },
            { key: 'checking_ssl', label: 'SSL Check', icon: 'bi-shield-lock' },
            { key: 'checking_headers', label: 'Headers', icon: 'bi-card-heading' },
            { key: 'scanning_network', label: 'Network Scan', icon: 'bi-ethernet' },
            { key: 'scanning_wordpress', label: 'WordPress', icon: 'bi-wordpress' },
            { key: 'scanning_sensitive_files', label: 'Exposed Files', icon: 'bi-file-earmark-lock' },
            { key: 'generating_suggestions', label: 'Suggestions', icon: 'bi-lightbulb' },
            { key: 'scanning_login', label: 'Login Security', icon: 'bi-key' },
            { key: 'sending_notifications', label: 'Notifications', icon: 'bi-envelope' },
            { key: 'calculating_score', label: 'Audit Score', icon: 'bi-graph-up' },
          ];
          const currentStage = scanProgress?.stage || 'initializing';
          const currentIdx = stages.findIndex(s => s.key === currentStage);
          const progressPct = Math.max(5, ((currentIdx + 1) / stages.length) * 100);
          const elapsed = scanProgress?.elapsed || 0;
          const mins = Math.floor(elapsed / 60);
          const secs = elapsed % 60;

          return (
            <div className="card border-0 shadow-sm mb-4" style={{ borderLeft: '4px solid #6366f1' }}>
              <div className="card-body py-3">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <div className="d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm text-primary me-2"></div>
                    <span className="fw-semibold">AI Security Scan in Progress</span>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    {scanProgress?.pagesScanned ? (
                      <small className="text-muted"><i className="bi bi-file-earmark me-1"></i>{scanProgress.pagesScanned} pages</small>
                    ) : null}
                    {scanProgress?.aiAnalyses ? (
                      <small className="text-muted"><i className="bi bi-cpu me-1"></i>{scanProgress.aiAnalyses} AI analyses</small>
                    ) : null}
                    <small className="text-muted"><i className="bi bi-clock me-1"></i>{mins > 0 ? `${mins}m ` : ''}{secs}s</small>
                  </div>
                </div>
                <div className="progress mb-2" style={{ height: 8 }}>
                  <div className="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                    style={{ width: `${progressPct}%`, transition: 'width 0.5s ease' }}></div>
                </div>
                <div className="d-flex gap-1 flex-wrap">
                  {stages.map((stage, i) => {
                    const isDone = i < currentIdx;
                    const isCurrent = i === currentIdx;
                    return (
                      <span key={stage.key} className={`badge ${isDone ? 'bg-success' : isCurrent ? 'bg-primary' : 'bg-light text-muted'}`}
                        style={{ fontSize: '0.7rem', transition: 'all 0.3s ease' }}>
                        <i className={`bi ${isDone ? 'bi-check' : stage.icon} me-1`}></i>
                        {stage.label}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}

        {/* ===== Stat Cards ===== */}
        <div className="row mb-4 g-3">
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #6366f1' }}>
              <div className="card-body py-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="text-muted small">AI Suggestions</div>
                    <h3 className="mb-0" style={{ color: '#6366f1' }}>{overview?.suggestions?.pending || 0}</h3>
                  </div>
                  <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, background: 'rgba(99, 102, 241, 0.1)' }}>
                    <i className="bi bi-lightbulb fs-5" style={{ color: '#6366f1' }}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #dc3545' }}>
              <div className="card-body py-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="text-muted small">Malware Detected</div>
                    <h3 className="mb-0 text-danger">{overview?.malware?.active || 0}</h3>
                  </div>
                  <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, background: 'rgba(220, 53, 69, 0.1)' }}>
                    <i className="bi bi-bug text-danger fs-5"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100" style={{ borderLeft: '4px solid #fd7e14' }}>
              <div className="card-body py-3">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <div className="text-muted small">Page Changes</div>
                    <h3 className="mb-0" style={{ color: '#fd7e14' }}>{overview?.changes?.total || 0}</h3>
                  </div>
                  <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, background: 'rgba(253, 126, 20, 0.1)' }}>
                    <i className="bi bi-file-diff fs-5" style={{ color: '#fd7e14' }}></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            {(() => {
              const sslIssues = suggestions.filter(s => s.issue_type === 'ssl_issue');
              const hasCritical = sslIssues.some(s => s.severity === 'critical');
              const hasHigh = sslIssues.some(s => s.severity === 'high');
              const sslOk = sslIssues.length === 0 && overview?.lastScanAt;
              const borderColor = hasCritical ? '#dc3545' : hasHigh ? '#fd7e14' : sslOk ? '#198754' : '#6c757d';
              return (
                <div className="card border-0 shadow-sm h-100" style={{ borderLeft: `4px solid ${borderColor}` }}>
                  <div className="card-body py-3">
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <div className="text-muted small">SSL / Security</div>
                        {sslOk ? (
                          <h3 className="mb-0 text-success"><i className="bi bi-shield-lock-fill me-1" style={{ fontSize: '1.2rem' }}></i>OK</h3>
                        ) : sslIssues.length > 0 ? (
                          <h3 className="mb-0" style={{ color: borderColor }}>{sslIssues.length} {sslIssues.length === 1 ? 'Issue' : 'Issues'}</h3>
                        ) : (
                          <h3 className="mb-0 text-muted">--</h3>
                        )}
                      </div>
                      <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: 40, height: 40, background: sslOk ? 'rgba(25, 135, 84, 0.1)' : sslIssues.length > 0 ? 'rgba(220, 53, 69, 0.1)' : 'rgba(108, 117, 125, 0.1)' }}>
                        <i className={`bi ${sslOk ? 'bi-shield-lock-fill text-success' : sslIssues.length > 0 ? 'bi-shield-exclamation text-danger' : 'bi-shield text-muted'} fs-5`}></i>
                      </div>
                    </div>
                    {sslIssues.length > 0 && (
                      <div className="mt-1">
                        {sslIssues.slice(0, 2).map((issue, i) => (
                          <small key={i} className={`d-block text-${getSeverityColor(issue.severity)}`} style={{ fontSize: '0.72rem' }}>
                            <i className="bi bi-dot"></i>{issue.title}
                          </small>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
          </div>
        </div>

        {/* ===== Network Security ===== */}
        {(endpointInfo.length > 0 || (networkScan && networkScan.length > 0)) && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body py-3 px-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="mb-0"><i className="bi bi-ethernet me-2 text-primary"></i>Network Security</h6>
                {networkScan && networkScan.length > 0 && (() => {
                  const worstRisk = networkScan.reduce((worst: string, r: any) => {
                    const levels = ['safe', 'low', 'medium', 'high', 'critical'];
                    return levels.indexOf(r.riskLevel) > levels.indexOf(worst) ? r.riskLevel : worst;
                  }, 'safe');
                  const riskColors: Record<string, string> = { safe: '#10b981', low: '#6c757d', medium: '#f59e0b', high: '#fd7e14', critical: '#dc3545' };
                  return <span className="badge" style={{ backgroundColor: riskColors[worstRisk] || '#6c757d' }}>{worstRisk.toUpperCase()}</span>;
                })()}
              </div>

              {/* Endpoints with IPs and Provider */}
              {endpointInfo.map((ep, i) => {
                const scanForIp = networkScan?.find((s: any) => ep.ipv4.includes(s.ip));
                return (
                  <div key={i} className="border rounded-3 p-3 mb-2">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <i className="bi bi-globe2 text-primary"></i>
                      <span className="fw-medium">{ep.hostname}</span>
                      {ep.ipv4.map((ip, j) => (
                        <span key={j} className="badge bg-primary bg-opacity-10 text-primary" style={{ fontSize: '0.72rem' }}>
                          {ip}
                        </span>
                      ))}
                      {ep.ipv6.length > 0 && (
                        <span className="badge bg-info bg-opacity-10 text-info" style={{ fontSize: '0.72rem' }}>
                          IPv6: {ep.ipv6[0]}
                        </span>
                      )}
                    </div>
                    {scanForIp?.provider && (
                      <div className="mb-2">
                        <span className="badge bg-dark bg-opacity-10 text-dark" style={{ fontSize: '0.75rem' }}>
                          <i className="bi bi-cloud me-1"></i>{scanForIp.provider.name}
                        </span>
                        {scanForIp.provider.asn && (
                          <span className="badge bg-secondary bg-opacity-10 text-secondary ms-1" style={{ fontSize: '0.72rem' }}>
                            {scanForIp.provider.asn}
                          </span>
                        )}
                      </div>
                    )}
                    {/* Open Ports */}
                    {scanForIp?.openPorts && scanForIp.openPorts.length > 0 && (
                      <div>
                        <small className="text-muted d-block mb-1">Open Ports:</small>
                        <div className="d-flex flex-wrap gap-1">
                          {scanForIp.openPorts.map((p: any, j: number) => {
                            const riskColor: Record<string, string> = { critical: '#dc3545', high: '#fd7e14', medium: '#f59e0b', low: '#6c757d' };
                            return (
                              <span
                                key={j}
                                className="badge"
                                style={{ backgroundColor: `${riskColor[p.risk] || '#6c757d'}20`, color: riskColor[p.risk] || '#6c757d', border: `1px solid ${riskColor[p.risk] || '#6c757d'}40`, fontSize: '0.72rem' }}
                                title={p.description}
                              >
                                {p.port} {p.service}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {scanForIp && scanForIp.openPorts?.length === 0 && (
                      <small className="text-success"><i className="bi bi-check-circle me-1"></i>No dangerous ports detected</small>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ===== WordPress Security (conditional) ===== */}
        {wordpressScan && wordpressScan.isWordPress && (
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-body py-3 px-4">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h6 className="mb-0"><i className="bi bi-wordpress me-2" style={{ color: '#21759b' }}></i>WordPress Security</h6>
                {(() => {
                  const riskColors: Record<string, string> = { safe: '#10b981', low: '#6c757d', medium: '#f59e0b', high: '#fd7e14', critical: '#dc3545' };
                  const risk = wordpressScan.riskLevel || 'unknown';
                  return <span className="badge" style={{ backgroundColor: riskColors[risk] || '#6c757d' }}>{risk.toUpperCase()}</span>;
                })()}
              </div>

              {/* Core version */}
              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <div className="bg-light rounded-3 p-3 text-center">
                    <div className="text-muted small">WordPress Core</div>
                    <div className="fw-bold fs-5 mt-1">{wordpressScan.coreVersion || 'Unknown'}</div>
                    {wordpressScan.aiAnalysis?.coreStatus?.isOutdated && (
                      <span className="badge bg-danger mt-1">Outdated</span>
                    )}
                    {wordpressScan.aiAnalysis?.coreStatus && !wordpressScan.aiAnalysis.coreStatus.isOutdated && (
                      <span className="badge bg-success mt-1">Up to date</span>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="bg-light rounded-3 p-3 text-center">
                    <div className="text-muted small">Plugins Detected</div>
                    <div className="fw-bold fs-5 mt-1">{wordpressScan.plugins?.length || 0}</div>
                    {(wordpressScan.aiAnalysis?.pluginIssues?.length || 0) > 0 && (
                      <span className="badge bg-warning text-dark mt-1">{wordpressScan.aiAnalysis.pluginIssues.length} issues</span>
                    )}
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="bg-light rounded-3 p-3 text-center">
                    <div className="text-muted small">Active Theme</div>
                    <div className="fw-bold mt-1" style={{ fontSize: '0.95rem' }}>{wordpressScan.theme?.name || 'Unknown'}</div>
                    {wordpressScan.theme?.version && (
                      <small className="text-muted">v{wordpressScan.theme.version}</small>
                    )}
                  </div>
                </div>
              </div>

              {/* Plugins table */}
              {wordpressScan.plugins && wordpressScan.plugins.length > 0 && (
                <div className="mb-3">
                  <small className="text-muted fw-semibold d-block mb-2">Installed Plugins</small>
                  <div className="table-responsive">
                    <table className="table table-sm table-hover mb-0" style={{ fontSize: '0.82rem' }}>
                      <thead className="table-light">
                        <tr>
                          <th>Plugin</th>
                          <th>Version</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wordpressScan.plugins.map((plugin: any, i: number) => {
                          const issue = wordpressScan.aiAnalysis?.pluginIssues?.find((p: any) =>
                            p.name === plugin.slug || p.name === plugin.name
                          );
                          return (
                            <tr key={i}>
                              <td className="fw-medium">{plugin.name}</td>
                              <td>{plugin.version || <span className="text-muted">Unknown</span>}</td>
                              <td>
                                {issue ? (
                                  <span className="badge" style={{
                                    backgroundColor: issue.severity === 'critical' ? '#dc354520' : issue.severity === 'high' ? '#fd7e1420' : '#f59e0b20',
                                    color: issue.severity === 'critical' ? '#dc3545' : issue.severity === 'high' ? '#fd7e14' : '#f59e0b',
                                  }} title={issue.issue}>
                                    {issue.severity.toUpperCase()}
                                  </span>
                                ) : (
                                  <span className="badge bg-success bg-opacity-10 text-success">Secure</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* AI Recommendations */}
              {wordpressScan.aiAnalysis?.recommendations && wordpressScan.aiAnalysis.recommendations.length > 0 && (
                <div className="bg-info bg-opacity-10 rounded-3 p-3">
                  <small className="fw-semibold text-info d-block mb-1"><i className="bi bi-robot me-1"></i>AI Recommendations</small>
                  <ul className="mb-0 ps-3" style={{ fontSize: '0.82rem' }}>
                    {wordpressScan.aiAnalysis.recommendations.map((rec: string, i: number) => (
                      <li key={i} className="text-muted">{rec}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ===== NEW: Threat Gauge + Page Monitor Grid ===== */}
        <div className="row mb-4 g-3">
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm h-100" style={{ background: 'linear-gradient(135deg, #fafbfc 0%, #f1f5f9 100%)' }}>
              <div className="card-header bg-transparent border-bottom py-3">
                <h6 className="mb-0"><i className="bi bi-speedometer2 me-2 text-danger"></i>Threat Level</h6>
              </div>
              <div className="card-body d-flex align-items-center justify-content-center py-2">
                <ThreatLevelGauge
                  threatScore={(() => {
                    let score = 0;
                    // Active malware (highest weight)
                    score += Math.min(40, (overview?.malware?.active || 0) * 20);
                    // Login security findings (admin panels exposed, critical findings)
                    const ls = overview?.loginSecurity;
                    if (ls) {
                      score += Math.min(20, (ls.exposedAdminPanels || 0) * 10 + (ls.criticalFindings?.length || 0) * 5);
                    }
                    // Suspicious page changes
                    score += Math.min(15, (overview?.changes?.unreviewed || 0) * 3);
                    // Pending AI suggestions by severity
                    const critSugg = suggestions.filter(s => s.severity === 'critical').length;
                    const highSugg = suggestions.filter(s => s.severity === 'high').length;
                    score += Math.min(15, critSugg * 8 + highSugg * 4);
                    // Audit score inverse (low audit = higher threat)
                    if (auditData) {
                      score += Math.min(10, Math.max(0, (100 - auditData.score.overallScore) / 10));
                    }
                    return Math.min(100, score);
                  })()}
                  totalFindings={totalFindings}
                  activeMalware={overview?.malware?.active || 0}
                  suspiciousPages={pagesSummary.suspicious || 0}
                />
              </div>
            </div>
          </div>
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <h6 className="mb-0"><i className="bi bi-grid-3x3-gap me-2 text-primary"></i>Page Monitor</h6>
                <span className="badge bg-light text-dark">{pagesSummary.total} pages tracked</span>
              </div>
              <div className="card-body py-2">
                <PageMonitorGrid pages={monitoredPages} summary={pagesSummary} />
              </div>
            </div>
          </div>
        </div>

        {/* ===== Charts Row ===== */}
        <div className="row mb-4 g-3">
          <div className="col-lg-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0"><i className="bi bi-pie-chart me-2 text-primary"></i>Issue Distribution</h6>
              </div>
              <div className="card-body d-flex align-items-center justify-content-center">
                <SeverityDonutChart data={overview?.severityDistribution || []} />
              </div>
            </div>
          </div>
          <div className="col-lg-5">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <h6 className="mb-0"><i className="bi bi-activity me-2 text-primary"></i>Scan Activity</h6>
                <small className="text-muted">Last 10 scans</small>
              </div>
              <div className="card-body">
                <ScanActivityChart data={overview?.scanHistory || []} />
              </div>
            </div>
          </div>
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <h6 className="mb-0">
                  <i className="bi bi-graph-up-arrow me-2 text-info"></i>Traffic Monitor
                </h6>
                {trafficAnalysis?.summary?.riskLevel && trafficAnalysis.summary.riskLevel !== 'normal' && (
                  <span className={`badge bg-${trafficAnalysis.summary.riskLevel === 'critical' ? 'danger' : trafficAnalysis.summary.riskLevel === 'high' ? 'warning' : 'info'}`}>
                    {trafficAnalysis.summary.riskLevel}
                  </span>
                )}
              </div>
              <div className="card-body py-2">
                {!trafficAnalysis || trafficAnalysis.hourlyData.length === 0 ? (
                  <div className="text-center py-3 text-muted">
                    <i className="bi bi-graph-up fs-2 d-block mb-1"></i>
                    <small>No traffic data available</small>
                  </div>
                ) : (
                  <>
                    {/* Mini traffic chart */}
                    <div className="d-flex align-items-end justify-content-between mb-2" style={{ height: 60, gap: 1 }}>
                      {trafficAnalysis.hourlyData.slice(-24).map((dp, i) => {
                        const maxViews = Math.max(...trafficAnalysis.hourlyData.slice(-24).map(d => d.pageViews), 1);
                        const height = Math.max((dp.pageViews / maxViews) * 100, 2);
                        const isAnomaly = trafficAnalysis.anomalies.some(a => a.timestamp === dp.hour);
                        return (
                          <div key={i} className="flex-fill" title={`${new Date(dp.hour).toLocaleTimeString('en-US', { hour: '2-digit' })}: ${dp.pageViews} views`}
                            style={{
                              height: `${height}%`, minHeight: 2,
                              background: isAnomaly ? '#dc3545' : 'linear-gradient(180deg, #06b6d4, #67e8f9)',
                              borderRadius: '2px 2px 0 0',
                            }}></div>
                        );
                      })}
                    </div>
                    {/* Summary stats */}
                    <div className="d-flex justify-content-between" style={{ fontSize: '0.72rem' }}>
                      <div className="text-center">
                        <div className="fw-bold text-primary">{trafficAnalysis.summary.totalPageViews.toLocaleString()}</div>
                        <span className="text-muted">Total Views (48h)</span>
                      </div>
                      <div className="text-center">
                        <div className="fw-bold">{trafficAnalysis.summary.avgHourlyViews}</div>
                        <span className="text-muted">Avg/Hour</span>
                      </div>
                      <div className="text-center">
                        <div className={`fw-bold ${trafficAnalysis.summary.trafficTrend === 'increasing' ? 'text-success' : trafficAnalysis.summary.trafficTrend === 'decreasing' ? 'text-danger' : 'text-muted'}`}>
                          <i className={`bi ${trafficAnalysis.summary.trafficTrend === 'increasing' ? 'bi-arrow-up' : trafficAnalysis.summary.trafficTrend === 'decreasing' ? 'bi-arrow-down' : 'bi-dash'}`}></i>
                          {trafficAnalysis.summary.trafficTrend}
                        </div>
                        <span className="text-muted">Trend</span>
                      </div>
                    </div>
                    {/* Anomalies */}
                    {trafficAnalysis.anomalies.length > 0 && (
                      <div className="mt-2 pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                        <small className="fw-semibold text-danger"><i className="bi bi-exclamation-triangle me-1"></i>{trafficAnalysis.anomalies.length} Anomalies Detected</small>
                        {trafficAnalysis.anomalies.slice(0, 3).map((a, i) => (
                          <div key={i} className="d-flex align-items-start mt-1" style={{ fontSize: '0.7rem' }}>
                            <span className={`badge bg-${a.severity === 'critical' ? 'danger' : a.severity === 'high' ? 'warning' : 'info'} me-1`} style={{ fontSize: '0.55rem' }}>{a.type}</span>
                            <span className="text-muted">{a.description.substring(0, 80)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ===== Security Audit Card (Square) ===== */}
        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: 12 }}>
          {auditData ? (
            <div className="card-body p-4">
              <div className="row g-4">
                {/* Left: Big Score */}
                <div className="col-md-3 text-center d-flex flex-column align-items-center justify-content-center" style={{ borderRight: '1px solid #e9ecef' }}>
                  <ScoreRing score={auditData.score.overallScore} size={130} strokeWidth={12} label="Audit Score" />
                  <span className={`badge mt-2 px-3 py-1 bg-${auditData.score.riskLevel === 'low' ? 'success' : auditData.score.riskLevel === 'medium' ? 'warning' : auditData.score.riskLevel === 'high' ? 'danger' : 'danger'}`} style={{ fontSize: '0.8rem' }}>
                    {auditData.score.riskLevel.toUpperCase()} RISK
                  </span>
                  <small className="text-muted mt-1">{new Date(auditData.scannedAt).toLocaleDateString('en-US', { dateStyle: 'medium' })}</small>
                </div>

                {/* Middle: Score Bars + Findings */}
                <div className="col-md-5">
                  <h6 className="mb-3"><i className="bi bi-clipboard-data me-2 text-primary"></i>Security Audit</h6>
                  <MiniScoreBar score={auditData.score.malwareScore} label="Malware Protection" color="#dc3545" />
                  <MiniScoreBar score={auditData.score.changeScore} label="Change Detection" color="#fd7e14" />
                  <MiniScoreBar score={auditData.score.integrityScore} label="Page Integrity" color="#6366f1" />

                  {/* Findings */}
                  {auditData.score.riskFactors?.length > 0 && (
                    <div className="mt-3 pt-2" style={{ borderTop: '1px solid #f0f0f0' }}>
                      <small className="text-danger fw-semibold"><i className="bi bi-exclamation-triangle me-1"></i>Findings</small>
                      {auditData.score.riskFactors.slice(0, 4).map((rf, i) => (
                        <div key={i} className="d-flex align-items-start mt-1" style={{ fontSize: '0.75rem' }}>
                          <i className="bi bi-exclamation-circle text-warning me-1 mt-0" style={{ fontSize: '0.65rem' }}></i>
                          <span className="text-muted">{rf}</span>
                        </div>
                      ))}
                      {auditData.score.riskFactors.length > 4 && (
                        <small className="text-muted ms-3">+{auditData.score.riskFactors.length - 4} more</small>
                      )}
                    </div>
                  )}
                </div>

                {/* Right: Audit History + Recommendations */}
                <div className="col-md-4" style={{ borderLeft: '1px solid #e9ecef' }}>
                  {/* Previous Scans */}
                  {(overview?.auditHistory || []).length > 0 && (
                    <div className="mb-3">
                      <small className="fw-semibold text-muted"><i className="bi bi-clock-history me-1"></i>Previous Audits</small>
                      <div className="mt-1">
                        {(overview?.auditHistory || []).map((h, i) => (
                          <div key={h.id || i} className="d-flex justify-content-between align-items-center py-1" style={{ fontSize: '0.75rem', borderBottom: '1px solid #f8f9fa' }}>
                            <span className="text-muted">{new Date(h.scannedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                            <div className="d-flex align-items-center gap-2">
                              <span className="fw-semibold">{h.score}/100</span>
                              <span className={`badge bg-${h.riskLevel === 'low' ? 'success' : h.riskLevel === 'medium' ? 'warning' : 'danger'} bg-opacity-10 text-${h.riskLevel === 'low' ? 'success' : h.riskLevel === 'medium' ? 'warning' : 'danger'}`} style={{ fontSize: '0.6rem' }}>
                                {h.riskLevel}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {auditData.score.recommendations?.length > 0 && (
                    <div>
                      <small className="fw-semibold text-success"><i className="bi bi-lightbulb me-1"></i>Recommendations</small>
                      {auditData.score.recommendations.slice(0, 3).map((rec, i) => (
                        <div key={i} className="d-flex align-items-start mt-1" style={{ fontSize: '0.72rem' }}>
                          <i className="bi bi-check-circle text-success me-1" style={{ fontSize: '0.6rem', marginTop: 2 }}></i>
                          <span className="text-muted">{rec}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Actions */}
                  <div className="mt-3 d-flex gap-2 flex-wrap">
                    <button className="btn btn-outline-primary btn-sm" onClick={triggerAIScan} disabled={scanning}>
                      {scanning ? <><span className="spinner-border spinner-border-sm me-1"></span>Scanning...</> : <><i className="bi bi-search me-1"></i>Full Audit</>}
                    </button>
                    <span className="badge bg-light text-muted border d-flex align-items-center" style={{ fontSize: '0.7rem' }}>
                      <i className="bi bi-calendar-check me-1"></i>Auto: 1st of month
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="card-body p-4">
              <div className="text-center py-3">
                <div className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 60, height: 60, background: 'rgba(99, 102, 241, 0.1)' }}>
                  <i className="bi bi-clipboard-check fs-3" style={{ color: '#6366f1' }}></i>
                </div>
                <h5 className="mb-1">No Security Audit Performed Yet</h5>
                <p className="text-muted mb-3">Run a security audit to get a best practices score, identify vulnerabilities, and get recommendations.</p>
                <div className="d-flex justify-content-center gap-2">
                  <button className="btn btn-primary" onClick={triggerAIScan} disabled={scanning}>
                    <i className="bi bi-play-circle me-1"></i>Run Audit Now
                  </button>
                  <span className="badge bg-light text-muted border d-flex align-items-center px-3" style={{ fontSize: '0.75rem' }}>
                    <i className="bi bi-calendar-check me-1"></i>Monthly auto-audit: 1st of each month
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ===== Tabs ===== */}
        <ul className="nav nav-pills mb-4 bg-light rounded-3 p-1">
          <li className="nav-item">
            <button className={`nav-link rounded-3 ${activeTab === 'suggestions' ? 'active' : ''}`} onClick={() => setActiveTab('suggestions')}>
              <i className="bi bi-lightbulb me-1"></i>AI Suggestions
              {suggestions.length > 0 && <span className={`badge ${activeTab === 'suggestions' ? 'bg-white text-primary' : 'bg-primary'} ms-1`}>{suggestions.length}</span>}
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link rounded-3 ${activeTab === 'malware' ? 'active' : ''}`} onClick={() => setActiveTab('malware')}>
              <i className="bi bi-bug me-1"></i>Malware
              {malwareDetections.filter(d => d.status === 'new').length > 0 && <span className="badge bg-danger ms-1">{malwareDetections.filter(d => d.status === 'new').length}</span>}
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link rounded-3 ${activeTab === 'changes' ? 'active' : ''}`} onClick={() => setActiveTab('changes')}>
              <i className="bi bi-file-diff me-1"></i>Page Changes
              {pageChanges.filter(c => !c.is_reviewed).length > 0 && <span className="badge bg-warning text-dark ms-1">{pageChanges.filter(c => !c.is_reviewed).length}</span>}
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link rounded-3 ${activeTab === 'exposed-files' ? 'active' : ''}`} onClick={() => setActiveTab('exposed-files')}>
              <i className="bi bi-file-earmark-lock me-1"></i>Exposed Files
              {sensitiveFiles.length > 0 && <span className={`badge ${activeTab === 'exposed-files' ? 'bg-white text-danger' : 'bg-danger'} ms-1`}>{sensitiveFiles.length}</span>}
            </button>
          </li>
          <li className="nav-item">
            <button className={`nav-link rounded-3 ${activeTab === 'history' ? 'active' : ''}`} onClick={() => setActiveTab('history')}>
              <i className="bi bi-clock-history me-1"></i>Scan History
            </button>
          </li>
        </ul>

        {/* === AI Suggestions Tab === */}
        {activeTab === 'suggestions' && (
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3">
              <h5 className="mb-0"><i className="bi bi-robot text-primary me-2"></i>Intelligent Security Recommendations</h5>
            </div>
            <div className="card-body p-0">
              {suggestions.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-check-circle text-success fs-1 d-block mb-3"></i>
                  <h5>All Clear!</h5>
                  <p className="text-muted">No pending AI recommendations at this time.</p>
                </div>
              ) : (
                <div className="accordion" id="suggestionsAccordion">
                  {suggestions.map((suggestion) => (
                    <div key={suggestion.id} className="accordion-item border-0 border-bottom">
                      <div className="accordion-header">
                        <button className={`accordion-button ${expandedSuggestion !== suggestion.id ? 'collapsed' : ''}`}
                          onClick={() => setExpandedSuggestion(expandedSuggestion === suggestion.id ? null : suggestion.id)}>
                          <div className="d-flex align-items-center w-100 me-3">
                            <span className={`badge bg-${getSeverityColor(suggestion.severity)} me-3`}>
                              <i className={`bi ${getSeverityIcon(suggestion.severity)} me-1`}></i>{suggestion.severity.toUpperCase()}
                            </span>
                            <div className="flex-grow-1">
                              <div className="fw-semibold">{suggestion.title}</div>
                              <small className="text-muted">{suggestion.affected_url}</small>
                            </div>
                            {suggestion.applied_at && <span className="badge bg-success me-2"><i className="bi bi-check me-1"></i>Applied</span>}
                          </div>
                        </button>
                      </div>
                      <div className={`accordion-collapse collapse ${expandedSuggestion === suggestion.id ? 'show' : ''}`}>
                        <div className="accordion-body">
                          <p className="text-muted">{suggestion.description}</p>
                          <div className="bg-light rounded p-3 mb-3">
                            <h6 className="text-primary mb-2"><i className="bi bi-robot me-2"></i>AI Recommendation</h6>
                            <p className="mb-0">{suggestion.ai_recommendation}</p>
                          </div>
                          {suggestion.action_steps?.length > 0 && (
                            <div className="mb-3">
                              <h6 className="mb-2">Action Steps:</h6>
                              <ol className="mb-0">{suggestion.action_steps.map((step, i) => <li key={i} className="mb-1">{step}</li>)}</ol>
                            </div>
                          )}
                          <div className="d-flex gap-2">
                            <button className="btn btn-success btn-sm" onClick={() => applySuggestion(suggestion.id)} disabled={!!suggestion.applied_at}>
                              <i className="bi bi-check-lg me-1"></i>Mark as Applied
                            </button>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => dismissSuggestion(suggestion.id)}>
                              <i className="bi bi-x-lg me-1"></i>Dismiss
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* === Malware Tab === */}
        {activeTab === 'malware' && (
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3">
              <h5 className="mb-0"><i className="bi bi-shield-exclamation text-danger me-2"></i>Malware & Threat Detections</h5>
            </div>
            <div className="card-body p-0">
              {malwareDetections.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-shield-check text-success fs-1 d-block mb-3"></i>
                  <h5>No Malware Detected</h5>
                  <p className="text-muted">Your website is clean. No malicious patterns found.</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr><th>Threat</th><th>URL</th><th>Severity</th><th>Status</th><th>Detected</th><th>Actions</th></tr>
                    </thead>
                    <tbody>
                      {malwareDetections.map((d) => (
                        <tr key={d.id}>
                          <td>
                            <div className="fw-medium">{d.signature_name}</div>
                            <small className="text-muted text-truncate d-block" style={{ maxWidth: 200 }}>{d.matched_content?.substring(0, 50)}...</small>
                          </td>
                          <td><small className="text-muted">{d.url}</small></td>
                          <td><span className={`badge bg-${getSeverityColor(d.severity)}`}>{d.severity.toUpperCase()}</span></td>
                          <td><span className={`badge bg-${d.status === 'new' ? 'danger' : d.status === 'resolved' ? 'success' : d.status === 'false_positive' ? 'secondary' : 'warning'}`}>{d.status.replace('_', ' ')}</span></td>
                          <td><small>{new Date(d.detected_at).toLocaleDateString()}</small></td>
                          <td>
                            {d.status === 'new' && (
                              <div className="btn-group btn-group-sm">
                                <button className="btn btn-outline-success" onClick={() => resolveMalware(d.id)} title="Resolve"><i className="bi bi-check-lg"></i></button>
                                <button className="btn btn-outline-secondary" onClick={() => markFalsePositive(d.id)} title="False Positive"><i className="bi bi-x-lg"></i></button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* === Page Changes Tab === */}
        {activeTab === 'changes' && (
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3">
              <h5 className="mb-0"><i className="bi bi-file-diff text-warning me-2"></i>Page Content Changes</h5>
            </div>
            <div className="card-body p-0">
              {pageChanges.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-file-check text-success fs-1 d-block mb-3"></i>
                  <h5>No Changes Detected</h5>
                  <p className="text-muted">Your page content has remained stable.</p>
                </div>
              ) : (
                <div className="list-group list-group-flush">
                  {pageChanges.map((change) => {
                    const summary = change.changes_summary;
                    const ai = change.ai_analysis;
                    const hasBreakdown = summary && (
                      (summary.scriptsAdded || 0) + (summary.scriptsRemoved || 0) +
                      (summary.iframesAdded || 0) + (summary.iframesRemoved || 0) +
                      (summary.externalLinksAdded || 0) + (summary.externalLinksRemoved || 0) +
                      (summary.formsModified || 0) + (summary.formsAdded || 0) + (summary.formsRemoved || 0)
                    ) > 0;

                    const riskColors: Record<string, string> = { safe: '#10b981', low: '#84cc16', medium: '#f59e0b', high: '#f97316', critical: '#ef4444' };
                    const riskColor = ai?.riskLevel ? (riskColors[ai.riskLevel] || '#6b7280') : undefined;

                    return (
                      <div key={change.id} className="list-group-item px-3 py-3" style={ai?.riskLevel && ai.riskLevel !== 'safe' && ai.riskLevel !== 'low' ? { borderLeft: `3px solid ${riskColor}` } : {}}>
                        <div className="d-flex justify-content-between align-items-start">
                          <div className="flex-grow-1">
                            {/* Header row */}
                            <div className="d-flex align-items-center mb-1 flex-wrap gap-1">
                              <span className={`badge bg-${change.change_type === 'new' ? 'success' : change.change_type === 'modified' ? 'warning' : change.change_type === 'deleted' ? 'danger' : 'secondary'}`}>{change.change_type}</span>
                              <span className="badge bg-info">{parseFloat(String(change.change_percent || 0)).toFixed(1)}% changed</span>
                              {ai?.riskLevel && (
                                <span className="badge" style={{ backgroundColor: riskColor, color: '#fff' }}>
                                  <i className={`bi ${ai.riskLevel === 'critical' || ai.riskLevel === 'high' ? 'bi-exclamation-triangle-fill' : 'bi-shield-check'} me-1`}></i>
                                  {ai.riskLevel.toUpperCase()} Risk
                                </span>
                              )}
                              {change.is_reviewed && <span className="badge bg-success"><i className="bi bi-check me-1"></i>Reviewed</span>}
                              {change.is_suspicious && <span className="badge bg-danger"><i className="bi bi-exclamation-triangle me-1"></i>Suspicious</span>}
                            </div>
                            <div className="fw-medium mb-1">{change.url}</div>

                            {/* AI Analysis Summary */}
                            {ai && (
                              <div className="rounded p-2 mt-2 mb-2" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)', border: '1px solid #e0e7ff' }}>
                                <div className="d-flex align-items-center mb-1">
                                  <i className="bi bi-cpu me-1" style={{ color: '#6366f1' }}></i>
                                  <small className="fw-semibold" style={{ color: '#6366f1' }}>AI Analysis</small>
                                  {ai.confidence > 0 && (
                                    <small className="text-muted ms-auto" style={{ fontSize: '0.7rem' }}>Confidence: {ai.confidence}%</small>
                                  )}
                                </div>
                                <div style={{ fontSize: '0.85rem', color: '#374151' }}>{ai.changeSummary}</div>

                                {/* Anomaly badges */}
                                {ai.anomalies?.length > 0 && (
                                  <div className="mt-2 d-flex flex-wrap gap-1">
                                    {ai.anomalies.map((anomaly, i) => (
                                      <span key={i} className="badge bg-warning bg-opacity-15 text-dark" style={{ fontSize: '0.72rem' }}>
                                        <i className="bi bi-exclamation-diamond me-1"></i>{anomaly}
                                      </span>
                                    ))}
                                  </div>
                                )}

                                {/* Special indicators */}
                                <div className="d-flex gap-2 mt-1 flex-wrap">
                                  {ai.adInjection && (
                                    <span className="badge bg-danger bg-opacity-10 text-danger" style={{ fontSize: '0.72rem' }}>
                                      <i className="bi bi-megaphone me-1"></i>Ad Injection
                                    </span>
                                  )}
                                  {ai.malwareIndicators && (
                                    <span className="badge bg-danger" style={{ fontSize: '0.72rem' }}>
                                      <i className="bi bi-bug-fill me-1"></i>Malware Indicators
                                    </span>
                                  )}
                                  {ai.trackingChanges && (
                                    <span className="badge bg-info bg-opacity-10 text-info" style={{ fontSize: '0.72rem' }}>
                                      <i className="bi bi-eye me-1"></i>Tracking: {ai.trackingChanges}
                                    </span>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Structural Breakdown */}
                            {hasBreakdown && (
                              <div className="bg-light rounded p-2 mt-2 mb-2">
                                <small className="fw-semibold text-muted d-block mb-1"><i className="bi bi-search me-1"></i>Analysis Breakdown</small>
                                <div className="d-flex flex-wrap gap-3" style={{ fontSize: '0.78rem' }}>
                                  {((summary?.scriptsAdded || 0) + (summary?.scriptsRemoved || 0)) > 0 && (
                                    <div className="d-flex align-items-center">
                                      <i className="bi bi-code-slash text-warning me-1"></i>
                                      <span>Scripts: </span>
                                      {(summary?.scriptsAdded || 0) > 0 && <span className="text-success ms-1">+{summary?.scriptsAdded}</span>}
                                      {(summary?.scriptsRemoved || 0) > 0 && <span className="text-danger ms-1">-{summary?.scriptsRemoved}</span>}
                                    </div>
                                  )}
                                  {((summary?.iframesAdded || 0) + (summary?.iframesRemoved || 0)) > 0 && (
                                    <div className="d-flex align-items-center">
                                      <i className="bi bi-window-stack text-info me-1"></i>
                                      <span>Iframes: </span>
                                      {(summary?.iframesAdded || 0) > 0 && <span className="text-success ms-1">+{summary?.iframesAdded}</span>}
                                      {(summary?.iframesRemoved || 0) > 0 && <span className="text-danger ms-1">-{summary?.iframesRemoved}</span>}
                                    </div>
                                  )}
                                  {((summary?.externalLinksAdded || 0) + (summary?.externalLinksRemoved || 0)) > 0 && (
                                    <div className="d-flex align-items-center">
                                      <i className="bi bi-link-45deg text-primary me-1"></i>
                                      <span>Links: </span>
                                      {(summary?.externalLinksAdded || 0) > 0 && <span className="text-success ms-1">+{summary?.externalLinksAdded}</span>}
                                      {(summary?.externalLinksRemoved || 0) > 0 && <span className="text-danger ms-1">-{summary?.externalLinksRemoved}</span>}
                                    </div>
                                  )}
                                  {((summary?.formsModified || 0) + (summary?.formsAdded || 0) + (summary?.formsRemoved || 0)) > 0 && (
                                    <div className="d-flex align-items-center">
                                      <i className="bi bi-input-cursor-text text-secondary me-1"></i>
                                      <span>Forms: </span>
                                      {(summary?.formsAdded || 0) > 0 && <span className="text-success ms-1">+{summary?.formsAdded}</span>}
                                      {(summary?.formsRemoved || 0) > 0 && <span className="text-danger ms-1">-{summary?.formsRemoved}</span>}
                                      {(summary?.formsModified || 0) > 0 && <span className="text-warning ms-1">{summary?.formsModified} modified</span>}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Suspicious changes */}
                            {change.suspicious_changes?.length > 0 && (
                              <div className="mt-1">
                                {change.suspicious_changes.map((sc, i) => (
                                  <span key={i} className="badge bg-danger bg-opacity-10 text-danger me-1 mb-1"><i className="bi bi-exclamation-triangle me-1"></i>{sc}</span>
                                ))}
                              </div>
                            )}
                            <small className="text-muted d-block mt-1">
                              Detected: {new Date(change.detected_at).toLocaleString()}
                              {change.ai_analyzed_at && <span className="ms-2"><i className="bi bi-cpu me-1"></i>AI analyzed</span>}
                            </small>
                          </div>
                          <div className="d-flex flex-column gap-1 ms-2 flex-shrink-0">
                            <button className="btn btn-outline-info btn-sm" onClick={() => viewChangeDiff(change.id)}>
                              <i className="bi bi-file-earmark-diff me-1"></i>View Diff
                            </button>
                            {!change.is_reviewed && (
                              <button className="btn btn-outline-primary btn-sm" onClick={() => reviewChange(change.id)}>
                                <i className="bi bi-check-lg me-1"></i>Reviewed
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* === Exposed Files Tab === */}
        {activeTab === 'exposed-files' && (
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
              <div>
                <h5 className="mb-0"><i className="bi bi-file-earmark-lock text-danger me-2"></i>Exposed Sensitive Files</h5>
                <small className="text-muted">Files that should not be publicly accessible</small>
              </div>
              <button className="btn btn-outline-danger btn-sm" onClick={async () => {
                if (!currentAsset) return;
                try {
                  await aiSecurityAPI.triggerSensitiveFileScan(currentAsset.id);
                  toast.success('Sensitive file scan started — results will appear shortly');
                  setTimeout(() => {
                    aiSecurityAPI.getSensitiveFiles(currentAsset.id).then(r => {
                      if (r.data.status === 'success') setSensitiveFiles(r.data.data?.findings || []);
                    }).catch(() => {});
                  }, 15000);
                } catch { toast.error('Failed to start scan'); }
              }}>
                <i className="bi bi-search me-1"></i>Quick Scan
              </button>
            </div>
            <div className="card-body p-0">
              {!sensitiveFilesLoaded ? (
                <div className="text-center py-5 text-muted">
                  <div className="spinner-border spinner-border-sm me-2"></div>Loading...
                </div>
              ) : sensitiveFiles.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-shield-check text-success fs-1 d-block mb-3"></i>
                  <h5>No Exposed Files Found</h5>
                  <p className="text-muted mb-3">No sensitive files are publicly accessible. Run a scan to check for newly exposed files.</p>
                  <button className="btn btn-outline-primary btn-sm" onClick={triggerAIScan} disabled={scanning}>
                    <i className="bi bi-search me-1"></i>Run Full Security Scan
                  </button>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>File Path</th>
                        <th>Severity</th>
                        <th>Description</th>
                        <th>Recommendation</th>
                        <th>Found</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sensitiveFiles.map((f: any) => (
                        <tr key={f.id}>
                          <td>
                            <div>
                              <code className="small">{f.context?.path || new URL(f.affected_url).pathname}</code>
                            </div>
                            <a href={f.affected_url} target="_blank" rel="noopener noreferrer" className="text-muted small">
                              <i className="bi bi-box-arrow-up-right me-1"></i>{f.affected_url.substring(0, 50)}{f.affected_url.length > 50 ? '...' : ''}
                            </a>
                            {f.context?.contentPreview && (
                              <div className="mt-1">
                                <code className="text-danger small d-block" style={{ fontSize: '0.65rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {f.context.contentPreview}
                                </code>
                              </div>
                            )}
                          </td>
                          <td>
                            <span className={`badge bg-${f.severity === 'critical' ? 'danger' : f.severity === 'high' ? 'warning text-dark' : f.severity === 'medium' ? 'info text-dark' : 'secondary'}`}>
                              {f.severity}
                            </span>
                          </td>
                          <td><small className="text-muted">{f.description}</small></td>
                          <td>
                            <small className="text-muted">{f.ai_recommendation?.substring(0, 100)}...</small>
                            {f.action_steps?.length > 0 && (
                              <details className="mt-1">
                                <summary className="text-primary small" style={{ cursor: 'pointer' }}>Steps</summary>
                                <ul className="mt-1 mb-0 ps-3">
                                  {f.action_steps.map((step: string, i: number) => (
                                    <li key={i} className="small text-muted">{step}</li>
                                  ))}
                                </ul>
                              </details>
                            )}
                          </td>
                          <td>
                            <small className="text-muted">{new Date(f.created_at).toLocaleDateString()}</small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* === Scan History Tab === */}
        {activeTab === 'history' && (
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
              <h5 className="mb-0"><i className="bi bi-clock-history text-primary me-2"></i>Scan History</h5>
              <button className="btn btn-primary btn-sm" onClick={triggerAIScan} disabled={scanning}>
                {scanning ? <><span className="spinner-border spinner-border-sm me-1"></span>Scanning...</> : <><i className="bi bi-play-circle me-1"></i>Run New Scan</>}
              </button>
            </div>
            <div className="card-body p-0">
              {scanRecords.length === 0 ? (
                <div className="text-center py-5">
                  <i className="bi bi-clock text-muted fs-1 d-block mb-3"></i>
                  <h5>No Scan History</h5>
                  <p className="text-muted">Run your first AI security scan to see results here.</p>
                  <button className="btn btn-primary" onClick={triggerAIScan} disabled={scanning}>
                    <i className="bi bi-play-circle me-2"></i>Run First Scan
                  </button>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-hover mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>Date & Time</th>
                          <th>Duration</th>
                          <th>Pages</th>
                          <th>Changes</th>
                          <th>Malware</th>
                          <th>AI</th>
                          <th>Suggestions</th>
                          <th>Status</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {scanRecords.map((scan) => {
                          const dMins = Math.floor(scan.duration / 60);
                          const dSecs = scan.duration % 60;
                          const durationText = dMins > 0 ? `${dMins}m ${dSecs}s` : `${dSecs}s`;
                          const isSelected = selectedScanDetail?.id === scan.id;
                          return (
                            <React.Fragment key={scan.id}>
                              <tr style={{ cursor: 'pointer' }} onClick={() => setSelectedScanDetail(isSelected ? null : scan)}
                                className={isSelected ? 'table-active' : ''}>
                                <td>
                                  <div className="fw-medium">{new Date(scan.startedAt).toLocaleDateString('en-US', { dateStyle: 'medium' })}</div>
                                  <small className="text-muted">{new Date(scan.startedAt).toLocaleTimeString('en-US', { timeStyle: 'short' })}</small>
                                </td>
                                <td><small className="text-muted">{scan.completedAt ? durationText : '--'}</small></td>
                                <td><span className="badge bg-light text-dark"><i className="bi bi-file-earmark me-1"></i>{scan.pagesScanned}</span></td>
                                <td>{scan.changesDetected > 0 ? <span className="badge bg-warning text-dark">{scan.changesDetected}</span> : <span className="text-muted">0</span>}</td>
                                <td>{scan.malwareDetected > 0 ? <span className="badge bg-danger">{scan.malwareDetected}</span> : <span className="text-success"><i className="bi bi-check-lg"></i></span>}</td>
                                <td>{scan.aiAnalyses > 0 ? <span className="badge bg-primary bg-opacity-75">{scan.aiAnalyses}</span> : <span className="text-muted">0</span>}</td>
                                <td>{scan.suggestionsGenerated > 0 ? <span className="badge bg-primary">{scan.suggestionsGenerated}</span> : <span className="text-muted">0</span>}</td>
                                <td>
                                  {scan.status === 'completed' ? (
                                    scan.malwareDetected > 0 ? <span className="badge bg-danger"><i className="bi bi-exclamation-triangle me-1"></i>Threats</span>
                                    : scan.issuesFound > 0 ? <span className="badge bg-warning text-dark">{scan.issuesFound} Issues</span>
                                    : <span className="badge bg-success"><i className="bi bi-check-circle me-1"></i>Clean</span>
                                  ) : scan.status === 'running' ? (
                                    <span className="badge bg-primary"><span className="spinner-border spinner-border-sm me-1" style={{ width: 10, height: 10 }}></span>Running</span>
                                  ) : scan.status === 'failed' ? (
                                    <span className="badge bg-danger"><i className="bi bi-x-circle me-1"></i>Failed</span>
                                  ) : (
                                    <span className="badge bg-secondary">{scan.status}</span>
                                  )}
                                </td>
                                <td>
                                  <i className={`bi ${isSelected ? 'bi-chevron-up' : 'bi-chevron-down'} text-muted`}></i>
                                </td>
                              </tr>
                              {isSelected && (
                                <tr>
                                  <td colSpan={9} className="p-0">
                                    <div className="p-3 bg-light" style={{ borderLeft: '3px solid #6366f1' }}>
                                      <h6 className="mb-2"><i className="bi bi-info-circle me-2 text-primary"></i>Scan Details</h6>
                                      <div className="row g-3">
                                        <div className="col-md-4">
                                          <div className="bg-white rounded p-2">
                                            <small className="text-muted d-block">Scan Type</small>
                                            <span className="fw-medium text-capitalize">{scan.scanType}</span>
                                          </div>
                                        </div>
                                        <div className="col-md-4">
                                          <div className="bg-white rounded p-2">
                                            <small className="text-muted d-block">Started</small>
                                            <span className="fw-medium">{new Date(scan.startedAt).toLocaleString()}</span>
                                          </div>
                                        </div>
                                        <div className="col-md-4">
                                          <div className="bg-white rounded p-2">
                                            <small className="text-muted d-block">Completed</small>
                                            <span className="fw-medium">{scan.completedAt ? new Date(scan.completedAt).toLocaleString() : 'In progress...'}</span>
                                          </div>
                                        </div>
                                        <div className="col-md-3">
                                          <div className="bg-white rounded p-2 text-center">
                                            <div className="fs-5 fw-bold text-primary">{scan.pagesScanned}</div>
                                            <small className="text-muted">Pages Scanned</small>
                                          </div>
                                        </div>
                                        <div className="col-md-3">
                                          <div className="bg-white rounded p-2 text-center">
                                            <div className={`fs-5 fw-bold ${scan.changesDetected > 0 ? 'text-warning' : 'text-success'}`}>{scan.changesDetected}</div>
                                            <small className="text-muted">Changes</small>
                                          </div>
                                        </div>
                                        <div className="col-md-3">
                                          <div className="bg-white rounded p-2 text-center">
                                            <div className={`fs-5 fw-bold ${scan.malwareDetected > 0 ? 'text-danger' : 'text-success'}`}>{scan.malwareDetected}</div>
                                            <small className="text-muted">Malware</small>
                                          </div>
                                        </div>
                                        <div className="col-md-3">
                                          <div className="bg-white rounded p-2 text-center">
                                            <div className={`fs-5 fw-bold ${scan.sslIssues > 0 ? 'text-warning' : 'text-success'}`}>{scan.sslIssues}</div>
                                            <small className="text-muted">SSL Issues</small>
                                          </div>
                                        </div>
                                      </div>
                                      {scan.error && (
                                        <div className="alert alert-danger mt-2 mb-0 py-2 small">
                                          <i className="bi bi-exclamation-triangle me-1"></i>{scan.error}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ===== Source Code Diff Modal ===== */}
        {showDiffModal && (
          <div className="modal d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            onClick={(e) => { if (e.target === e.currentTarget) { setShowDiffModal(false); setSelectedChange(null); } }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header border-bottom">
                  <h5 className="modal-title">
                    <i className="bi bi-file-earmark-diff me-2 text-primary"></i>
                    Change Detail {selectedChange ? `- ${selectedChange.url}` : ''}
                  </h5>
                  <button type="button" className="btn-close" onClick={() => { setShowDiffModal(false); setSelectedChange(null); }}></button>
                </div>
                <div className="modal-body p-0">
                  {loadingDiff ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary"></div>
                      <p className="text-muted mt-2">Loading change details...</p>
                    </div>
                  ) : selectedChange ? (
                    <div>
                      {/* Change Info Bar */}
                      <div className="p-3 bg-light border-bottom d-flex gap-3 flex-wrap align-items-center">
                        <span className={`badge bg-${selectedChange.change_type === 'new' ? 'success' : selectedChange.change_type === 'modified' ? 'warning' : 'danger'}`}>
                          {selectedChange.change_type}
                        </span>
                        <span className="badge bg-info">{parseFloat(String(selectedChange.change_percent || 0)).toFixed(1)}% changed</span>
                        {selectedChange.ai_analysis?.riskLevel && (
                          <span className={`badge ${selectedChange.ai_analysis.riskLevel === 'safe' || selectedChange.ai_analysis.riskLevel === 'low' ? 'bg-success' : selectedChange.ai_analysis.riskLevel === 'medium' ? 'bg-warning text-dark' : 'bg-danger'}`}>
                            {selectedChange.ai_analysis.riskLevel.toUpperCase()} Risk
                          </span>
                        )}
                        {selectedChange.suspicious_changes?.map((sc, i) => (
                          <span key={i} className="badge bg-danger bg-opacity-10 text-danger"><i className="bi bi-exclamation-triangle me-1"></i>{sc}</span>
                        ))}
                      </div>

                      {/* AI Analysis */}
                      {selectedChange.ai_analysis && (
                        <div className="p-3 border-bottom" style={{ background: 'linear-gradient(135deg, #f0f4ff 0%, #faf5ff 100%)' }}>
                          <h6 className="mb-1"><i className="bi bi-cpu me-2" style={{ color: '#6366f1' }}></i>AI Analysis</h6>
                          <p className="mb-1" style={{ fontSize: '0.9rem' }}>{selectedChange.ai_analysis.changeSummary}</p>
                          {selectedChange.ai_analysis.anomalies?.length > 0 && (
                            <div className="d-flex gap-1 flex-wrap mt-1">
                              {selectedChange.ai_analysis.anomalies.map((a, i) => (
                                <span key={i} className="badge bg-warning bg-opacity-15 text-dark" style={{ fontSize: '0.72rem' }}>{a}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Source Code Comparison */}
                      <div className="p-3">
                        {(!selectedChange.previous_content && !selectedChange.current_content) ? (
                          <div className="text-center py-4 text-muted">
                            <i className="bi bi-file-earmark-x fs-1 d-block mb-2"></i>
                            Source code not available for this change.
                          </div>
                        ) : (
                          <div className="row g-3">
                            <div className="col-md-6">
                              <h6 className="mb-2"><span className="badge bg-danger me-2">Previous</span>Source Code</h6>
                              <div className="border rounded" style={{ maxHeight: 500, overflow: 'auto', background: '#1e1e1e' }}>
                                <pre className="p-2 mb-0" style={{ fontSize: '0.75rem', color: '#d4d4d4', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                  {selectedChange.previous_content ? selectedChange.previous_content.substring(0, 50000) : '(No previous version available)'}
                                </pre>
                              </div>
                            </div>
                            <div className="col-md-6">
                              <h6 className="mb-2"><span className="badge bg-success me-2">Current</span>Source Code</h6>
                              <div className="border rounded" style={{ maxHeight: 500, overflow: 'auto', background: '#1e1e1e' }}>
                                <pre className="p-2 mb-0" style={{ fontSize: '0.75rem', color: '#d4d4d4', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                  {selectedChange.current_content ? selectedChange.current_content.substring(0, 50000) : '(Source code not available)'}
                                </pre>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => { setShowDiffModal(false); setSelectedChange(null); }}>Close</button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
};

export default AISecurityAdvisorPage;
